import React, { useState, useEffect } from 'react';
import './QueryWalkthroughModal.css';
import { useCounties } from '../../contexts/CountiesContext';
import voterService from '../../api/voterService';

function QueryWalkthroughModal({ isOpen, onClose, onApplyFilters, elections, isElectionsLoading, formatDate }) {
  // Use counties context
  const { getCountiesForState, getAllCountiesWithStates, isLoading: countiesLoading, error: countiesError, allCounties, availableStates } = useCounties();


  // State management for steps and selections
  const [currentStep, setCurrentStep] = useState(1);
  const [showElections, setShowElections] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [selections, setSelections] = useState({
    state: '',
    electionType: '',
    race: '',
    county: '',
    city: '',
    district: '',
    politicalAffiliation: [],
    ageRange: { min: 18, max: 90 },
    electionCategories: [],  // Array of {level, type, minVoted, lastN}
    additionalFilters: {}
  });

  // Electoral districts based on selected race type
  const [districts, setDistricts] = useState([]);

  // Use states from counties context, with fallback including AR
  const states = availableStates.length > 0 ? availableStates : ['AR', 'FL', 'NC', 'OH', 'OK', 'PA'];

  // Election types
  const electionTypes = [
    { id: 'national', name: 'National (US Congress/Senate)' },
    { id: 'state', name: 'State (Governor, State Executive, State Legislature)' },
    { id: 'county', name: 'County' },
    { id: 'local', name: 'Local (City, Town, School Board)' }
  ];

  // Race types mapped to election types
  const raceTypes = {
    national: [
      { id: 'senate', name: 'US Senate' },
      { id: 'house', name: 'US House of Representatives' }
    ],
    state: [
      { id: 'governor', name: 'Governor' },
      { id: 'lt-governor', name: 'Lieutenant Governor' },
      { id: 'state-senate', name: 'State Senate' },
      { id: 'state-house', name: 'State House' }
    ],
    county: [
      { id: 'county-commissioner', name: 'County Commissioner' },
      { id: 'county-executive', name: 'County Executive' },
      { id: 'district-attorney', name: 'District Attorney' },
      { id: 'sheriff', name: 'Sheriff' }
    ],
    local: [
      { id: 'mayor', name: 'Mayor' },
      { id: 'city-council', name: 'City Council' },
      { id: 'school-board', name: 'School Board' },
      { id: 'town-supervisor', name: 'Town Supervisor' }
    ]
  };

  // Political affiliation options
  const partyOptions = [
    { id: 'DEM', name: 'Democrat' },
    { id: 'REP', name: 'Republican' },
    { id: 'IND', name: 'Independent' },
    { id: 'LIB', name: 'Libertarian' },
    { id: 'GRE', name: 'Green' },
    { id: 'OTH', name: 'Other' }
  ];

  // Election category options
  const electionLevelOptions = [
    { id: 'NATIONAL', name: 'National' },
    { id: 'STATE', name: 'State' },
    { id: 'LOCAL', name: 'Local' },
    { id: 'SPECIAL', name: 'Special' }
  ];

  const electionTypeOptions = {
    NATIONAL: [{ id: 'GENERAL', name: 'General' }],
    STATE: [
      { id: 'PRIMARY', name: 'Primary' },
      { id: 'RUNOFF', name: 'Runoff' }
    ],
    LOCAL: [
      { id: 'GENERAL', name: 'General' },
      { id: 'PRIMARY', name: 'Primary' },
      { id: 'RUNOFF', name: 'Runoff' }
    ],
    SPECIAL: [{ id: 'SPECIAL', name: 'Special' }]
  };

  // Get counties for current selection - reactive to state changes
  const [counties, setCounties] = useState([]);

  // Cities state - fetched when county is selected
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [citiesError, setCitiesError] = useState(null);

  useEffect(() => {
    if (currentStep === 4 && selections.electionType) {
      if (countiesLoading) {
        return;
      }

      if (selections.state) {
        // Get counties for selected state
        const stateCounties = getCountiesForState(selections.state);
        setCounties(stateCounties);
      } else {
        // Get all counties with state labels
        const allCountiesWithLabels = getAllCountiesWithStates().map(item => item.label);
        setCounties(allCountiesWithLabels);
      }
    } else {
      // Clear counties when not in step 4
      setCounties([]);
    }
  }, [currentStep, selections.electionType, selections.state, getCountiesForState, getAllCountiesWithStates, countiesLoading, allCounties]);

  // Fetch cities when county is selected
  useEffect(() => {
    const fetchCities = async () => {
      if (!selections.state || !selections.county) {
        setCities([]);
        return;
      }

      // Extract just the county name if it's formatted as "COUNTY (STATE)"
      const countyName = selections.county.includes(' (')
        ? selections.county.split(' (')[0]
        : selections.county;

      try {
        setCitiesLoading(true);
        setCitiesError(null);
        const response = await voterService.getCities(selections.state, countyName);
        if (response.success) {
          setCities(response.data || []);
        } else {
          setCitiesError(response.error || 'Failed to fetch cities');
          setCities([]);
        }
      } catch (error) {
        console.error('Error fetching cities:', error);
        setCitiesError('Failed to load cities');
        setCities([]);
      } finally {
        setCitiesLoading(false);
      }
    };

    fetchCities();
  }, [selections.state, selections.county]);

  // Update districts when election type or race changes
  useEffect(() => {
    if (selections.electionType && selections.race) {
      generateDistricts();
    }
  }, [selections.electionType, selections.race]);

  // Browser back button handling
  useEffect(() => {
    if (!isOpen) return;

    const handlePopState = (event) => {
      if (currentStep > 1) {
        event.preventDefault();
        handlePrevStep();
        // Push a new state to prevent the modal from closing
        window.history.pushState({ modalStep: currentStep - 1 }, '', window.location.href);
      }
    };

    // Push initial state when modal opens
    window.history.pushState({ modalStep: currentStep }, '', window.location.href);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [isOpen, currentStep]);

  // Save modal state to localStorage
  const saveModalState = (step, selections) => {
    const modalState = {
      currentStep: step,
      selections: selections,
      timestamp: Date.now()
    };
    localStorage.setItem('queryWalkthroughState', JSON.stringify(modalState));
  };

  // Load modal state from localStorage
  const loadModalState = () => {
    try {
      const saved = localStorage.getItem('queryWalkthroughState');
      if (saved) {
        const state = JSON.parse(saved);
        // Only use saved state if it's less than 1 hour old
        if (Date.now() - state.timestamp < 3600000) {
          // Migrate old state format (voteHistory) to new format (electionCategories)
          if (state.selections && !state.selections.electionCategories) {
            state.selections.electionCategories = [];
            delete state.selections.voteHistory;
          }
          return state;
        }
      }
    } catch (e) {
      console.warn('Could not load modal state:', e);
    }
    return null;
  };

  // Reset to step 1 when modal is opened, or restore previous state
  useEffect(() => {
    if (isOpen) {
      const savedState = loadModalState();
      if (savedState) {
        console.log('Restoring modal state from step', savedState.currentStep);
        setCurrentStep(savedState.currentStep);
        setSelections(savedState.selections);
      } else {
        setCurrentStep(1);
        setSelections({
          state: '',
          electionType: '',
          race: '',
          county: '',
          city: '',
          district: '',
          politicalAffiliation: [],
          ageRange: { min: 18, max: 90 },
          electionCategories: [],
          additionalFilters: {}
        });
      }
      setShowElections(false);
    }
  }, [isOpen]);


  // Generate districts based on election type and race
  const generateDistricts = () => {
    let districtList = [];

    // Example district generation - would be replaced with actual API call
    if (selections.electionType === 'national') {
      if (selections.race === 'house') {
        // Congressional districts
        districtList = Array.from({ length: 27 }, (_, i) => ({
          id: `CD-${i + 1}`,
          name: `Congressional District ${i + 1}`
        }));
      }
    } else if (selections.electionType === 'state') {
      if (selections.race === 'state-senate') {
        // State Senate districts
        districtList = Array.from({ length: 40 }, (_, i) => ({
          id: `SD-${i + 1}`,
          name: `State Senate District ${i + 1}`
        }));
      } else if (selections.race === 'state-house') {
        // State House districts
        districtList = Array.from({ length: 120 }, (_, i) => ({
          id: `HD-${i + 1}`,
          name: `State House District ${i + 1}`
        }));
      }
    }

    setDistricts(districtList);
  };

  // Toggle elections display
  const toggleElections = () => {
    setShowElections(!showElections);
  };

  // Filter elections by selected state
  const getFilteredElections = () => {
    if (!elections || elections.length === 0) return [];

    if (selections.state) {
      return elections.filter(election => election.state === selections.state);
    }

    return elections.slice(0, 20); // Show first 20 if no state selected
  };

  // Handle next step with animation
  const handleNextStep = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    // Add fill animation to progress line
    const currentLineIndex = currentStep - 1;
    if (currentLineIndex < 4) {
      const progressLines = document.querySelectorAll('.progress-line');
      if (progressLines[currentLineIndex]) {
        progressLines[currentLineIndex].classList.add('filling');
        setTimeout(() => {
          progressLines[currentLineIndex].classList.remove('filling');
          progressLines[currentLineIndex].classList.add('completed');
        }, 800);
      }
    }

    setTimeout(() => {
      const newStep = currentStep + 1;
      setCurrentStep(newStep);
      setIsTransitioning(false);
      // Save state when moving to next step
      saveModalState(newStep, selections);
    }, 400);
  };

  // Handle previous step with animation
  const handlePrevStep = () => {
    if (isTransitioning) return;

    setIsTransitioning(true);

    // Remove completed state from current line
    const prevLineIndex = currentStep - 2;
    if (prevLineIndex >= 0) {
      const progressLines = document.querySelectorAll('.progress-line');
      if (progressLines[prevLineIndex]) {
        progressLines[prevLineIndex].classList.remove('completed', 'filling');
      }
    }

    setTimeout(() => {
      const newStep = currentStep - 1;
      setCurrentStep(newStep);
      setIsTransitioning(false);
      // Save state when moving to previous step
      saveModalState(newStep, selections);
    }, 300);
  };

  // Handle step click navigation
  const handleStepClick = (step) => {
    if (isTransitioning || step === currentStep) return;

    // Only allow going to previous steps or next step if current is complete
    if (step > currentStep && !isStepComplete()) return;
    if (step > currentStep + 1) return; // Don't allow skipping steps

    setIsTransitioning(true);

    // Update progress lines based on direction
    const progressLines = document.querySelectorAll('.progress-line');

    if (step > currentStep) {
      // Moving forward - fill lines with animation
      for (let i = currentStep - 1; i < step - 1; i++) {
        if (progressLines[i]) {
          setTimeout(() => {
            progressLines[i].classList.add('filling');
            setTimeout(() => {
              progressLines[i].classList.remove('filling');
              progressLines[i].classList.add('completed');
            }, 800);
          }, i * 200);
        }
      }
    } else {
      // Moving backward - remove completed state
      for (let i = currentStep - 2; i >= step - 1; i--) {
        if (progressLines[i]) {
          progressLines[i].classList.remove('completed', 'filling');
        }
      }
    }

    setTimeout(() => {
      setCurrentStep(step);
      setIsTransitioning(false);
      // Save state when clicking to different step
      saveModalState(step, selections);
    }, step > currentStep ? 400 : 300);
  };

  // Handle selection changes
  const handleSelectionChange = (field, value) => {
    let newSelections = {
      ...selections,
      [field]: value
    };

    // Reset dependent fields
    if (field === 'state') {
      newSelections = {
        ...newSelections,
        county: '',
        city: '',
        district: ''
      };
    } else if (field === 'county') {
      // Reset city when county changes
      newSelections = {
        ...newSelections,
        city: ''
      };
    } else if (field === 'electionType') {
      newSelections = {
        ...newSelections,
        race: '',
        district: ''
      };
    } else if (field === 'race') {
      newSelections = {
        ...newSelections,
        district: ''
      };
    }

    setSelections(newSelections);
    // Save state when selections change
    saveModalState(currentStep, newSelections);
  };

  // Handle checkbox group changes (for multi-select)
  const handleCheckboxChange = (field, value) => {
    setSelections(prev => {
      const currentValues = [...prev[field]];
      let newSelections;

      if (currentValues.includes(value)) {
        // Remove the value if already selected
        newSelections = {
          ...prev,
          [field]: currentValues.filter(item => item !== value)
        };
      } else {
        // Add the value if not selected
        newSelections = {
          ...prev,
          [field]: [...currentValues, value]
        };
      }
      
      // Save state when checkbox selections change
      saveModalState(currentStep, newSelections);
      return newSelections;
    });
  };

  // Handle range input changes
  const handleRangeChange = (field, key, value) => {
    const numValue = parseInt(value, 10);

    setSelections(prev => {
      const newRange = { ...prev[field] };

      if (key === 'min') {
        newRange.min = Math.min(numValue, newRange.max);
      } else if (key === 'max') {
        newRange.max = Math.max(numValue, newRange.min);
      }

      const newSelections = {
        ...prev,
        [field]: newRange
      };

      // Save state when range changes
      saveModalState(currentStep, newSelections);
      return newSelections;
    });
  };

  // Handle adding a new election category filter
  const handleAddCategory = () => {
    setSelections(prev => {
      const newCategories = [
        ...(prev.electionCategories || []),
        { level: '', type: '', minVoted: 1, lastN: 4 }
      ];
      const newSelections = {
        ...prev,
        electionCategories: newCategories
      };
      saveModalState(currentStep, newSelections);
      return newSelections;
    });
  };

  // Handle removing an election category filter
  const handleRemoveCategory = (index) => {
    setSelections(prev => {
      const newCategories = (prev.electionCategories || []).filter((_, i) => i !== index);
      const newSelections = {
        ...prev,
        electionCategories: newCategories
      };
      saveModalState(currentStep, newSelections);
      return newSelections;
    });
  };

  // Handle updating an election category filter
  const handleCategoryChange = (index, field, value) => {
    setSelections(prev => {
      const newCategories = [...(prev.electionCategories || [])];
      newCategories[index] = {
        ...newCategories[index],
        [field]: value
      };
      // Reset type when level changes
      if (field === 'level') {
        newCategories[index].type = '';
      }
      const newSelections = {
        ...prev,
        electionCategories: newCategories
      };
      saveModalState(currentStep, newSelections);
      return newSelections;
    });
  };

  // Apply filters and close modal
  const handleApplyFilters = () => {
    // Convert selections into filter format expected by the API
    const filters = {
      State: selections.state,
      County_Desc: (() => {
        const countyValue = selections.county;
        // Extract just the county name if it's formatted as "COUNTY (STATE)"
        return countyValue.includes(' (') ? countyValue.split(' (')[0] : countyValue;
      })(),
      City: selections.city || '',
      PolitalAff: selections.politicalAffiliation.length > 0 ? selections.politicalAffiliation.join(',') : '',
      // Add more filters based on selections
    };

    // Add district filter if applicable
    if (selections.district) {
      if (selections.electionType === 'national' && selections.race === 'house') {
        filters.CongressionalDistrict = selections.district;
      } else if (selections.electionType === 'state' && selections.race === 'state-senate') {
        filters.StateSenateDistrict = selections.district;
      } else if (selections.electionType === 'state' && selections.race === 'state-house') {
        filters.StateHouseDistrict = selections.district;
      }
    }

    // Add age range if modified from default
    if (selections.ageRange.min !== 18 || selections.ageRange.max !== 90) {
      filters.AgeMin = selections.ageRange.min;
      filters.AgeMax = selections.ageRange.max;
    }

    // Add election category filters if selected (uses OR logic between categories)
    const electionCategories = selections.electionCategories || [];
    if (electionCategories.length > 0) {
      // Only include categories that have both level and type set
      const validCategories = electionCategories.filter(cat => cat.level && cat.type);
      if (validCategories.length > 0) {
        filters.electionCategories = validCategories;
      }
    }

    // Pass filters to parent component
    onApplyFilters(filters);
    onClose();
  };

  // Calculate step completion status
  const isStepComplete = () => {
    switch (currentStep) {
      case 1:
        return !!selections.state;
      case 2:
        return !!selections.electionType;
      case 3:
        return !!selections.race;
      case 4:
        // Always allow proceeding from step 4
        // If counties are available and user hasn't selected one, they can still proceed
        // If no counties are available (like AR), user can proceed without selection
        return true;
      case 5:
        // Optional steps are always "complete"
        return true;
      default:
        return false;
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Voter Query Walkthrough</h2>
          <div className="header-actions">
            <button
              className="elections-toggle-button"
              onClick={toggleElections}
              disabled={isElectionsLoading}
            >
              {isElectionsLoading ? 'Loading...' : (showElections ? 'Hide Elections' : 'View Elections')}
            </button>
            <button className="close-button" onClick={onClose}>&times;</button>
          </div>
        </div>

        {/* Elections Display */}
        {showElections && (
          <div className="elections-section">
            <h3>Available Elections {selections.state && `in ${selections.state}`}</h3>
            {isElectionsLoading ? (
              <div className="elections-loading">Loading elections...</div>
            ) : (
              <div className="elections-table-container">
                <table className="elections-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>State</th>
                      <th>Classification</th>
                      <th>Type</th>
                      <th>Level</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getFilteredElections().map((election, index) => (
                      <tr key={election.election_id || index}>
                        <td>{formatDate ? formatDate(election.date) : election.date}</td>
                        <td>{election.state}</td>
                        <td>{election.classification}</td>
                        <td>{election.election_type}</td>
                        <td>{election.election_level}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {getFilteredElections().length === 0 && (
                  <div className="no-elections">
                    {selections.state ?
                      `No elections found for ${selections.state}` :
                      'No elections available'
                    }
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="modal-progress">
          <div className="progress-step" onClick={() => handleStepClick(1)}>
            <div className={`step-indicator ${currentStep >= 1 ? 'active' : ''
              } ${currentStep > 1 ? 'completed' : ''
              }`}>1</div>
            <div className="step-label">State</div>
          </div>
          <div className={`progress-line ${currentStep > 1 ? 'completed' : ''
            }`}></div>

          <div className="progress-step" onClick={() => handleStepClick(2)}>
            <div className={`step-indicator ${currentStep >= 2 ? 'active' : ''
              } ${currentStep > 2 ? 'completed' : ''
              }`}>2</div>
            <div className="step-label">Election Type</div>
          </div>
          <div className={`progress-line ${currentStep > 2 ? 'completed' : ''
            }`}></div>

          <div className="progress-step" onClick={() => handleStepClick(3)}>
            <div className={`step-indicator ${currentStep >= 3 ? 'active' : ''
              } ${currentStep > 3 ? 'completed' : ''
              }`}>3</div>
            <div className="step-label">Race</div>
          </div>
          <div className={`progress-line ${currentStep > 3 ? 'completed' : ''
            }`}></div>

          <div className="progress-step" onClick={() => handleStepClick(4)}>
            <div className={`step-indicator ${currentStep >= 4 ? 'active' : ''
              } ${currentStep > 4 ? 'completed' : ''
              }`}>4</div>
            <div className="step-label">Geography</div>
          </div>
          <div className={`progress-line ${currentStep > 4 ? 'completed' : ''
            }`}></div>

          <div className="progress-step" onClick={() => handleStepClick(5)}>
            <div className={`step-indicator ${currentStep >= 5 ? 'active' : ''
              }`}>5</div>
            <div className="step-label">Demographics</div>
          </div>
        </div>

        <div className="modal-content">
          {/* Step 1: Select State */}
          {currentStep === 1 && (
            <div className="step-content">
              <h3>What state is the election in?</h3>
              <p>Select the state where your campaign or election is taking place.</p>
              <div className="selection-grid">
                {states.map(state => (
                  <div
                    key={state}
                    className={`selection-item ${selections.state === state ? 'selected' : ''}`}
                    onClick={() => handleSelectionChange('state', state)}
                  >
                    {state}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 2: Election Type */}
          {currentStep === 2 && (
            <div className="step-content">
              <h3>What type of election is this for?</h3>
              <p>Select the level of government for this election.</p>
              <div className="selection-list">
                {electionTypes.map(type => (
                  <div
                    key={type.id}
                    className={`selection-card ${selections.electionType === type.id ? 'selected' : ''}`}
                    onClick={() => handleSelectionChange('electionType', type.id)}
                  >
                    <div className="card-title">{type.name}</div>
                    <div className="card-description">
                      {type.id === 'national' && 'Federal-level elected positions'}
                      {type.id === 'state' && 'State-level elected positions'}
                      {type.id === 'county' && 'County-level elected positions'}
                      {type.id === 'local' && 'City/town/municipal elected positions'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: Race Selection */}
          {currentStep === 3 && (
            <div className="step-content">
              <h3>Which specific race are you targeting?</h3>
              <p>Select the specific elected position for your campaign.</p>
              <div className="selection-list">
                {selections.electionType && raceTypes[selections.electionType] &&
                  raceTypes[selections.electionType].map(race => (
                    <div
                      key={race.id}
                      className={`selection-card ${selections.race === race.id ? 'selected' : ''}`}
                      onClick={() => handleSelectionChange('race', race.id)}
                    >
                      <div className="card-title">{race.name}</div>
                    </div>
                  ))}
              </div>
            </div>
          )}

          {/* Step 4: Geography */}
          {currentStep === 4 && (
            <div className="step-content">
              <h3>Select the geographic area</h3>

              {/* Show county selection for ALL election types */}
              <p>Select the county to target:</p>

              {countiesLoading && (
                <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px' }}>
                  <div>Loading counties data...</div>
                </div>
              )}

              {countiesError && (
                <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #dc3545', borderRadius: '4px' }}>
                  <div>Error loading counties: {countiesError}</div>
                </div>
              )}

              {!countiesLoading && !countiesError && counties.length === 0 && selections.state && (
                <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e8f5e8', border: '1px solid #28a745', borderRadius: '4px' }}>
                  <div>No counties available for {selections.state}</div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
                    This state does not have county data available. You can proceed to the next step.
                  </div>
                </div>
              )}

              {!countiesLoading && !countiesError && counties.length === 0 && !selections.state && (
                <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px' }}>
                  <div>Please select a state first to see available counties</div>
                </div>
              )}

              {!countiesLoading && counties.length > 0 && (
                <select
                  value={selections.county}
                  onChange={(e) => handleSelectionChange('county', e.target.value)}
                  className="select-dropdown"
                >
                  <option value="">Select County</option>
                  {counties.map((county, index) => (
                    <option key={index} value={county}>{county}</option>
                  ))}
                </select>
              )}

              {/* City Dropdown - Only show after County is selected */}
              {selections.county && (
                <div style={{ marginTop: '20px' }}>
                  <p>Select a specific city within {selections.county} (optional):</p>

                  {citiesLoading && (
                    <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#e3f2fd', border: '1px solid #2196f3', borderRadius: '4px' }}>
                      <div>Loading cities...</div>
                    </div>
                  )}

                  {citiesError && (
                    <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#f8d7da', border: '1px solid #dc3545', borderRadius: '4px' }}>
                      <div>Error loading cities: {citiesError}</div>
                    </div>
                  )}

                  {!citiesLoading && !citiesError && cities.length === 0 && (
                    <div style={{ marginBottom: '10px', padding: '10px', backgroundColor: '#fff3cd', border: '1px solid #ffc107', borderRadius: '4px' }}>
                      <div>No cities found for this county</div>
                    </div>
                  )}

                  {!citiesLoading && cities.length > 0 && (
                    <select
                      value={selections.city}
                      onChange={(e) => handleSelectionChange('city', e.target.value)}
                      className="select-dropdown"
                    >
                      <option value="">Select City (Optional)</option>
                      {cities.map((city, index) => (
                        <option key={index} value={city}>{city}</option>
                      ))}
                    </select>
                  )}
                </div>
              )}

            </div>
          )}

          {/* Step 5: Demographics and Additional Filters */}
          {currentStep === 5 && (
            <div className="step-content">
              <h3>Target specific demographics (Optional)</h3>

              {/* Political affiliation */}
              <div className="filter-section">
                <h4>Political Affiliation</h4>
                <div className="checkbox-group">
                  {partyOptions.map(party => (
                    <label key={party.id} className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={selections.politicalAffiliation.includes(party.id)}
                        onChange={() => handleCheckboxChange('politicalAffiliation', party.id)}
                      />
                      {party.name}
                    </label>
                  ))}
                </div>
              </div>

              {/* Age Range */}
              <div className="filter-section">
                <h4>Age Range: {selections.ageRange.min} - {selections.ageRange.max} years old</h4>
                <div style={{ margin: '20px 0', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #e9ecef' }}>
                  <div style={{ position: 'relative', marginBottom: '30px' }}>
                    {/* Dual range slider container */}
                    <div className="dual-range-slider" style={{ position: 'relative', height: '40px' }}>
                      {/* Background track */}
                      <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: '0',
                        right: '0',
                        height: '4px',
                        background: '#ddd',
                        borderRadius: '2px'
                      }} />

                      {/* Active range */}
                      <div style={{
                        position: 'absolute',
                        top: '20px',
                        left: `${((selections.ageRange.min - 18) / (120 - 18)) * 100}%`,
                        width: `${((selections.ageRange.max - selections.ageRange.min) / (120 - 18)) * 100}%`,
                        height: '4px',
                        background: '#112030',
                        borderRadius: '2px'
                      }} />

                      {/* Min range input */}
                      <input
                        type="range"
                        min="18"
                        max="120"
                        value={selections.ageRange.min}
                        onChange={(e) => handleRangeChange('ageRange', 'min', e.target.value)}
                        className="dual-range-min"
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '40px',
                          background: 'transparent',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />

                      {/* Max range input */}
                      <input
                        type="range"
                        min="18"
                        max="120"
                        value={selections.ageRange.max}
                        onChange={(e) => handleRangeChange('ageRange', 'max', e.target.value)}
                        className="dual-range-max"
                        style={{
                          position: 'absolute',
                          width: '100%',
                          height: '40px',
                          background: 'transparent',
                          outline: 'none',
                          cursor: 'pointer'
                        }}
                      />
                    </div>

                    {/* Value labels */}
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '14px', color: '#495057', marginTop: '8px', fontWeight: '500' }}>
                      <span>18</span>
                      <span>Selected: {selections.ageRange.min} - {selections.ageRange.max} years</span>
                      <span>120</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Election Category Filter */}
              <div className="filter-section">
                <h4>Election Category Filter</h4>
                <p style={{ fontSize: '14px', color: '#666', marginBottom: '10px' }}>
                  Filter voters by their participation in specific types of elections.
                  {(selections.electionCategories || []).length > 1 && (
                    <span style={{ fontWeight: '500', color: '#112030' }}> Multiple categories use OR logic - voters matching ANY category will be included.</span>
                  )}
                </p>

                {/* Display existing category filters */}
                {(selections.electionCategories || []).map((category, index) => (
                  <div key={index} style={{
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    padding: '15px',
                    marginBottom: '15px',
                    backgroundColor: '#f8f9fa'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '500' }}>Category Filter {index + 1}</span>
                      <button
                        onClick={() => handleRemoveCategory(index)}
                        style={{
                          background: '#dc3545',
                          border: 'none',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '20px',
                          fontWeight: 'bold',
                          padding: '2px 10px',
                          borderRadius: '4px',
                          lineHeight: '1'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginBottom: '15px' }}>
                      {/* Election Level */}
                      <div>
                        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '500' }}>Election Level</label>
                        <select
                          value={category.level}
                          onChange={(e) => handleCategoryChange(index, 'level', e.target.value)}
                          style={{
                            width: '100%',
                            padding: '10px 35px 10px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            boxSizing: 'border-box',
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 12px center'
                          }}
                        >
                          <option value="">Select Level</option>
                          {electionLevelOptions.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                          ))}
                        </select>
                      </div>

                      {/* Election Type */}
                      <div>
                        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '500' }}>Election Type</label>
                        <select
                          value={category.type}
                          onChange={(e) => handleCategoryChange(index, 'type', e.target.value)}
                          disabled={!category.level}
                          style={{
                            width: '100%',
                            padding: '10px 35px 10px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            backgroundColor: !category.level ? '#f5f5f5' : '#fff',
                            cursor: !category.level ? 'not-allowed' : 'pointer',
                            boxSizing: 'border-box',
                            opacity: !category.level ? 0.7 : 1,
                            appearance: 'none',
                            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E")`,
                            backgroundRepeat: 'no-repeat',
                            backgroundPosition: 'right 12px center'
                          }}
                        >
                          <option value="">Select Type</option>
                          {category.level && electionTypeOptions[category.level]?.map(opt => (
                            <option key={opt.id} value={opt.id}>{opt.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                      {/* Min Voted */}
                      <div>
                        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '500' }}>Minimum Times Voted</label>
                        <input
                          type="number"
                          min="1"
                          max="10"
                          value={category.minVoted}
                          onChange={(e) => handleCategoryChange(index, 'minVoted', parseInt(e.target.value) || 1)}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>

                      {/* Last N Elections */}
                      <div>
                        <label style={{ fontSize: '12px', color: '#666', display: 'block', marginBottom: '6px', fontWeight: '500' }}>Out of Last N Elections</label>
                        <input
                          type="number"
                          min="1"
                          max="8"
                          value={category.lastN}
                          onChange={(e) => handleCategoryChange(index, 'lastN', Math.min(parseInt(e.target.value) || 4, 8))}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #ddd',
                            borderRadius: '6px',
                            fontSize: '14px',
                            boxSizing: 'border-box'
                          }}
                        />
                      </div>
                    </div>

                    {/* Summary */}
                    {category.level && category.type && (
                      <div style={{
                        marginTop: '10px',
                        padding: '8px',
                        backgroundColor: '#e8f5e8',
                        borderRadius: '4px',
                        fontSize: '13px',
                        color: '#155724'
                      }}>
                        Voters who voted at least <strong>{category.minVoted}</strong> time(s) in <strong>{electionLevelOptions.find(o => o.id === category.level)?.name} {electionTypeOptions[category.level]?.find(o => o.id === category.type)?.name}</strong> elections out of the last <strong>{category.lastN}</strong>
                      </div>
                    )}
                  </div>
                ))}

                {/* Add Category Button */}
                <button
                  onClick={handleAddCategory}
                  style={{
                    width: '100%',
                    padding: '12px',
                    backgroundColor: '#112030',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  + Add Election Category Filter
                </button>

                {(selections.electionCategories || []).length === 0 && (
                  <div style={{
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '6px',
                    fontSize: '13px',
                    color: '#856404'
                  }}>
                    No election category filters added. Click the button above to add a filter, or skip this step if you don't need to filter by voting history.
                  </div>
                )}
              </div>

            </div>
          )}
        </div>

        <div className="modal-footer">
          {currentStep > 1 && (
            <button
              className="back-button"
              onClick={handlePrevStep}
              disabled={isTransitioning}
            >
              Back
            </button>
          )}

          {currentStep < 5 ? (
            <button
              className="next-button"
              onClick={handleNextStep}
              disabled={!isStepComplete() || isTransitioning}
            >
              {isTransitioning ? 'Loading...' : 'Next'}
            </button>
          ) : (
            <button
              className="apply-button"
              onClick={handleApplyFilters}
              disabled={isTransitioning}
            >
              Apply Filters
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default QueryWalkthroughModal;